import * as stylex from "@stylexjs/stylex";
import { springConfigs } from "charts/lib/motion";
import { AnimatePresence, motion } from "framer-motion";
import { useRTL } from "hooks";
import Link from "next/link";
import { E, pipe } from "prelude";
import type React from "react";
import { useMemo } from "react";
import Highlighter from "react-highlight-words";
import { getAliasHref, RAlias } from "routes";
import { breakpoints, colors } from "../../materials/consts.stylex";
import lunr from "../../search/lunr";

interface Page {
  id: string;
  title: string;
  locale: string;
  description: string;
  pagename: string;
  filename: string;
}
interface Props {
  searchIndex: {
    idx: lunr.Index;
    pages: Record<string, Page>;
  };
  query: string;
}

const styles = stylex.create({
  inputRoot: {
    marginRight: "0",
    marginLeft: "0",
    marginTop: {
      default: "16px",
      [breakpoints.l]: "0",
    },
  },
  inputRootSpacingRtl: {
    marginRight: {
      default: "0",
      [breakpoints.l]: "16px",
    },
  },
  inputRootSpacingLtr: {
    marginLeft: {
      default: "0",
      [breakpoints.l]: "16px",
    },
  },
  input: {
    borderRadius: "16px",
    borderStyle: "none",
    outlineStyle: "none",
    opacity: 0.5,
    transition: "0.4s opacity",
    width: {
      default: "100%",
      [breakpoints.l]: "auto",
    },
    padding: {
      default: "8px 16px",
      [breakpoints.l]: "4px 16px",
    },
    ":active": {
      opacity: 1,
    },
  },
  listRoot: {
    maxWidth: "640px",
    overflow: "auto",
    backgroundColor: "white",
    borderRadius: "4px",
    color: "black",
    zIndex: 2,
    boxShadow: "0 15px 40px -10px rgba(0, 0, 0, 0.28)", // FIXME: define global shadow styles
    position: {
      default: "static",
      [breakpoints.l]: "absolute",
    },
    width: {
      default: "100%",
      [breakpoints.l]: "500px",
    },
    maxHeight: {
      default: "300px",
      [breakpoints.l]: "500px",
    },
    marginTop: {
      default: "8px",
      [breakpoints.l]: "0",
    },
  },
  listRootRtl: {
    right: "auto",
    left: "0",
  },
  listRootLtr: {
    right: "0",
    left: "auto",
  },
  listButton: {
    borderStyle: "none",
    backgroundColor: "transparent",
    ":focus": {
      outlineStyle: "none",
      backgroundColor: colors.grayscale2,
    },
  },
  listButtonRtl: {
    textAlign: "right",
  },
  listButtonLtr: {
    textAlign: "left",
  },
  listItem: {
    padding: "8px",
    cursor: "pointer",
    // Consumed by the `.search-result-item mark` rule in style.global.css — keeps the
    // color's source of truth here instead of duplicating the hex value in that file.
    "--search-mark-bg": colors.grayscale3,
    ":hover": {
      backgroundColor: colors.grayscale2,
      opacity: 0.8,
    },
  },
  listItemTitle: {
    display: "block",
  },
});

const Results = (props: Props) => {
  const { searchIndex, query } = props;
  const idx = useMemo(() => lunr.Index.load(searchIndex.idx), [searchIndex.idx]);
  const results = idx.search(query ? `${query}*~2` : "");
  const isRTL = useRTL();

  return (
    <ul {...stylex.props(styles.listRoot, isRTL ? styles.listRootRtl : styles.listRootLtr)}>
      <AnimatePresence>
        {results.map((d) => {
          const match = Object.values(searchIndex.pages).find((x) => x.id === d.ref);
          const keywords = Object.keys(d.matchData.metadata);

          if (!match || ["figure", "figures"].includes(match.filename)) {
            return null;
          }

          return pipe(
            RAlias.decode(match.filename),
            E.fold(
              () => null,
              (alias) => (
                <motion.li
                  key={d.ref}
                  initial={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  // The <mark> highlight color/weight is set in style.global.css, scoped by
                  // this class — react-highlight-words' <Highlighter> renders <mark> itself,
                  // an element we don't render, so StyleX can't reach it directly.
                  className={`search-result-item ${stylex.props(styles.listItem).className}`}
                  layout
                  transition={springConfigs.slow}
                >
                  <Link
                    href={getAliasHref(alias)}
                    {...stylex.props(styles.listButton, isRTL ? styles.listButtonRtl : styles.listButtonLtr)}
                    tabIndex={0}
                  >
                    <b {...stylex.props(styles.listItemTitle)}>
                      <Highlighter searchWords={keywords} autoEscape={true} textToHighlight={match.title ?? ""} />
                    </b>
                    <Highlighter
                      searchWords={keywords}
                      autoEscape={true}
                      textToHighlight={`${match.description.substring(0, 100)} [...]`}
                    />
                  </Link>
                </motion.li>
              ),
            ),
          );
        })}
      </AnimatePresence>
    </ul>
  );
};

export const SearchComponent = (props: React.PropsWithChildren) => {
  const isRTL = useRTL();
  return (
    <div
      {...stylex.props(styles.inputRoot, isRTL ? styles.inputRootSpacingRtl : styles.inputRootSpacingLtr)}
      {...props}
    />
  );
};

export const SearchInput = (props: React.HTMLProps<HTMLInputElement>) => {
  return <input {...stylex.props(styles.input)} type="search" {...props} />;
};

export default Results;
